import type { AudioState, AudioStatus } from './audio-playback';
import type { EffectCue, MusicCue } from '../shared/protocol';
interface Dependencies { fetch: typeof fetch; clock: () => number; createContext: () => AudioContext }
interface Entry { abort: AbortController; promise: Promise<AudioBuffer>; buffer?: AudioBuffer }
interface Playing { source: AudioBufferSourceNode; gain: GainNode; key: string; cue: MusicCue; phase: string; questionId?: string; offset: number; started: number }

export class MusicPlayback {
  private context?: AudioContext;
  private master?: GainNode;
  private effectsMaster?: GainNode;
  private effects = new Map<string, { source: AudioBufferSourceNode; gain: GainNode }>();
  private completedEffects = new Set<string>();
  private effectPhase = '';
  private state?: AudioState;
  private receivedAt = 0;
  private cache = new Map<string, Entry>();
  private active?: Playing;
  private fading = new Set<Playing>();
  private disposed = false;
  private ducked = false;
  private status: AudioStatus = 'off';
  private failed = new Set<string>();
  private loopOrigins = new Map<string, number>();
  private suspendedLoop?: { key: string; offset: number };

  constructor(private token: string, private onStatus: (status: AudioStatus) => void, private deps: Dependencies) {}

  enable() {
    if (this.disposed) return;
    for (const url of this.failed) { this.cache.get(url)?.abort.abort(); this.cache.delete(url); }
    this.failed.clear();
    try {
      if (!this.context) {
        this.context = this.deps.createContext();
        this.master = this.context.createGain();
        this.master.gain.value = 0;
        this.master.connect(this.context.destination);
        this.effectsMaster = this.context.createGain();
        this.effectsMaster.gain.value = 0;
        this.effectsMaster.connect(this.context.destination);
      }
      void this.context.resume().then(() => this.sync()).catch(() => this.setStatus('blocked'));
    } catch { this.setStatus('unavailable'); }
  }

  update(state: AudioState) {
    if (this.disposed) return;
    this.state = state;
    this.receivedAt = this.deps.clock();
    this.sync();
  }

  setDucked(ducked: boolean) { this.ducked = ducked; this.volume(); }

  dispose() {
    this.disposed = true;
    this.stopAll();
    for (const entry of this.cache.values()) entry.abort.abort();
    this.cache.clear();
    void this.context?.close();
  }

  private setStatus(status: AudioStatus) {
    if (!this.disposed && status !== this.status) { this.status = status; this.onStatus(status); }
  }

  private volume() {
    if (!this.context || !this.master || !this.state) return;
    const target = !this.state.enabled || this.state.muted ? 0 : this.state.volume * (this.ducked ? .25 : .65);
    this.master.gain.cancelScheduledValues(this.context.currentTime);
    this.master.gain.setTargetAtTime(target, this.context.currentTime, this.ducked ? .04 : .12);
    // Round lead-ins are baked into intro music before the separate narration begins.
    const protectVoice = this.ducked || this.state.snapshot?.phase === 'intro';
    const effectsTarget = !this.state.enabled || this.state.muted ? 0 : this.state.volume * (protectVoice ? .15 : 1);
    this.effectsMaster?.gain.setTargetAtTime(effectsTarget, this.context.currentTime, protectVoice ? .02 : .12);
  }

  private stop(playing: Playing) {
    try { playing.source.stop(); } catch { /* Already ended. */ }
    playing.source.disconnect(); playing.gain.disconnect(); this.fading.delete(playing);
  }

  private stopAll() {
    if (this.active) this.stop(this.active);
    this.active = undefined;
    for (const playing of [...this.fading]) this.stop(playing);
    for (const playing of this.effects.values()) { playing.source.onended = null; try { playing.source.stop(); } catch {} playing.source.disconnect(); playing.gain.disconnect(); }
    this.effects.clear();
  }

  private load(cue: MusicCue) {
    let entry = this.cache.get(cue.url);
    if (entry) return entry;
    const abort = new AbortController();
    entry = { abort, promise: this.deps.fetch(cue.url, { headers: { Authorization: `Bearer ${this.token}` }, signal: abort.signal, cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error('Music unavailable'); return response.arrayBuffer(); })
      .then(data => this.context!.decodeAudioData(data)) };
    this.cache.set(cue.url, entry);
    const current = entry;
    void entry.promise.then(buffer => {
      if (this.disposed || abort.signal.aborted) return;
      current.buffer = buffer;
      this.sync();
    }).catch(() => {
      if (this.disposed || abort.signal.aborted) return;
      this.failed.add(cue.url);
      if (this.state?.snapshot?.musicCue?.url === cue.url) this.retire(false);
      this.sync();
    });
    return entry;
  }

  private sync() {
    const state = this.state;
    if (!state || this.disposed) return;
    this.volume();
    if (!state.enabled) { this.stopAll(); this.setStatus('off'); return; }
    if (!state.online || state.snapshot?.paused) {
      if (this.active?.cue.loop && this.context) this.suspendedLoop = { key: this.active.key, offset: this.active.offset + this.context.currentTime - this.active.started };
      this.stopAll(); this.setStatus('paused'); return;
    }
    if (!this.context || this.context.state !== 'running') { this.setStatus('blocked'); return; }
    const s = state.snapshot;
    const cue: MusicCue | undefined = s?.musicCue;
    const preloads: MusicCue[] = (s as typeof s & { musicPreloads?: MusicCue[] })?.musicPreloads ?? [];
    const effects: EffectCue[] = (s as typeof s & { effectCues?: EffectCue[] })?.effectCues ?? [];
    const wanted = [cue, ...effects, ...preloads].filter((value): value is MusicCue => !!value).slice(0, 8);
    const urls = new Set(wanted.map(value => value.url));
    for (const [url, entry] of this.cache) if (!urls.has(url)) { entry.abort.abort(); this.cache.delete(url); this.failed.delete(url); }
    for (const next of wanted) if (!this.failed.has(next.url)) this.load(next);
    this.syncEffects(effects);
    const effectFailed = effects.some(effect => this.failed.has(effect.url));
    const playingStatus: AudioStatus = state.muted || state.volume === 0 ? 'muted' : effectFailed ? 'unavailable' : 'playing';
    if (!cue || !s) { this.retire(false); this.setStatus(effectFailed ? 'unavailable' : 'ready'); return; }
    const buffer = this.cache.get(cue.url)?.buffer;
    if (!buffer) {
      if (this.failed.has(cue.url)) this.retire(false);
      this.setStatus(this.failed.has(cue.url) ? 'unavailable' : 'loading'); return;
    }
    const now = state.now + Math.max(0, this.deps.clock() - this.receivedAt);
    const key = cue.loop ? `${s.gameId}:${s.phase}:${cue.url}` : `${s.gameId}:${s.question?.id}:${s.phase}:${cue.url}`;
    if (this.suspendedLoop) {
      if (this.suspendedLoop.key === key) this.loopOrigins.set(key, now - this.suspendedLoop.offset * 1000);
      this.suspendedLoop = undefined;
    }
    if (!this.loopOrigins.has(key) && cue.loop) this.loopOrigins.set(key, now);
    const elapsed = cue.loop ? (now - this.loopOrigins.get(key)!) / 1000
      : s.deadline === null || s.phaseDurationMs === null ? 0 : (s.phaseDurationMs - (s.deadline - now) - (cue.startOffsetMs ?? 0)) / 1000;
    const offset = cue.loop ? Math.max(0, elapsed) % buffer.duration : Math.max(0, elapsed);
    if (!cue.loop && offset >= buffer.duration) { this.retire(false); this.setStatus('complete'); return; }
    if (this.active?.key === key) {
      const actual = this.active.offset + this.context.currentTime - this.active.started;
      const difference = cue.loop ? Math.abs((actual % buffer.duration) - offset) : Math.abs(actual - offset);
      if (difference < .75) { this.setStatus(playingStatus); return; }
      this.stop(this.active); this.active = undefined;
    }
    this.retire(!!this.active?.cue.preserveTail && ((s.phase === 'reveal' && !s.reveal?.voided && this.active?.questionId === s.question?.id && this.active?.phase === 'question') || (s.phase === 'finished' && this.active?.phase === 'finale')));
    const source = this.context.createBufferSource();
    source.buffer = buffer; source.loop = !!cue.loop;
    const gain = this.context.createGain();
    const start = this.context.currentTime + Math.max(0, -elapsed);
    gain.gain.setValueAtTime(0, this.context.currentTime);
    gain.gain.linearRampToValueAtTime(1, start + (cue.fadeInMs ?? 250) / 1000);
    source.connect(gain); gain.connect(this.master!);
    const playing: Playing = { source, gain, key, cue, phase: s.phase, questionId: s.question?.id, offset, started: start };
    this.active = playing;
    source.onended = () => {
      if (this.active === playing) { this.active = undefined; this.setStatus('complete'); }
      this.fading.delete(playing); source.disconnect(); gain.disconnect();
    };
    source.start(start, offset);
    this.setStatus(playingStatus);
  }

  private syncEffects(cues: EffectCue[]) {
    const s = this.state?.snapshot;
    if (!s || !this.context) return;
    const phase = `${s.gameId}:${s.questionIndex}:${s.phase}`;
    if (this.effectPhase !== phase) {
      for (const playing of this.effects.values()) { playing.source.onended = null; try { playing.source.stop(); } catch {} playing.source.disconnect(); playing.gain.disconnect(); }
      this.effects.clear(); this.completedEffects.clear(); this.effectPhase = phase;
    }
    const now = this.state!.now + Math.max(0, this.deps.clock() - this.receivedAt);
    const elapsed = s.deadline !== null && s.phaseDurationMs !== null ? s.phaseDurationMs - (s.deadline - now) : 0;
    for (const cue of cues) {
      const key = `${phase}:${cue.assetId ?? cue.url}`;
      if (this.effects.has(key) || this.completedEffects.has(key)) continue;
      const offset = (elapsed - (cue.offsetMs ?? 0)) / 1000;
      if (offset >= cue.durationMs / 1000) { this.completedEffects.add(key); continue; }
      const buffer = this.cache.get(cue.url)?.buffer;
      if (!buffer) continue;
      const source = this.context.createBufferSource(); source.buffer = buffer;
      const gain = this.context.createGain(); gain.gain.value = cue.gain ?? .65;
      source.connect(gain); gain.connect(this.effectsMaster!);
      this.effects.set(key, { source, gain });
      source.onended = () => { this.effects.delete(key); this.completedEffects.add(key); source.disconnect(); gain.disconnect(); };
      source.start(this.context.currentTime + Math.max(0, -offset), Math.max(0, offset));
    }
  }

  private retire(preserveTail: boolean) {
    const playing = this.active;
    if (!playing || !this.context) return;
    this.active = undefined;
    const time = this.context.currentTime;
    const remaining = (playing.source.buffer?.duration ?? 0) - playing.offset - (time - playing.started);
    const fade = preserveTail && remaining <= 3.5 ? Math.max(.25, remaining) : .25;
    playing.gain.gain.cancelScheduledValues(time);
    playing.gain.gain.setValueAtTime(playing.gain.gain.value, time);
    playing.gain.gain.linearRampToValueAtTime(0, time + fade);
    playing.source.stop(time + fade);
    this.fading.add(playing);
  }
}
